/**
 * @module AdapterDomainIcon
 * @package @geenius/adapters/react-css
 * @description Renders token-colored SVG icons for adapter domains so the BEM
 * variant does not leak shared string descriptors into visual icon slots.
 */

import type { AdapterDomain } from "@geenius/adapters";
import type { JSX } from "react";

export interface AdapterDomainIconProps {
  domain: AdapterDomain;
}

function DomainIconPaths({ domain }: AdapterDomainIconProps): JSX.Element {
  switch (domain) {
    case "db":
      return (
        <>
          <ellipse cx="12" cy="5" rx="7" ry="3" />
          <path d="M5 5v6c0 1.66 3.13 3 7 3s7-1.34 7-3V5" />
          <path d="M5 11v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
        </>
      );
    case "auth":
      return (
        <>
          <path d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
          <path d="M5 21a7 7 0 0 1 14 0" />
        </>
      );
    case "payments":
      return (
        <>
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <path d="M3 10h18" />
          <path d="M7 15h3" />
        </>
      );
    case "ai":
      return (
        <>
          <path d="M12 3v3" />
          <path d="M12 18v3" />
          <path d="M3 12h3" />
          <path d="M18 12h3" />
          <path d="M7.76 7.76 5.64 5.64" />
          <path d="m18.36 18.36-2.12-2.12" />
          <path d="m16.24 7.76 2.12-2.12" />
          <path d="m5.64 18.36 2.12-2.12" />
          <circle cx="12" cy="12" r="3.5" />
        </>
      );
    case "storage":
      return (
        <>
          <path d="M4 7.5A3.5 3.5 0 0 1 7.5 4H11l2 2h3.5A3.5 3.5 0 0 1 20 9.5v6A3.5 3.5 0 0 1 16.5 19h-9A3.5 3.5 0 0 1 4 15.5v-8Z" />
          <path d="M8 13h8" />
        </>
      );
    case "admin":
      return (
        <>
          <path d="M12 3 20 7v5c0 4.5-3.25 7.5-8 9-4.75-1.5-8-4.5-8-9V7l8-4Z" />
          <path d="M9 12.5 11 14.5 15.5 10" />
        </>
      );
    case "logger":
      return (
        <>
          <path d="M6 4h12v16H6z" />
          <path d="M9 8h6" />
          <path d="M9 12h6" />
          <path d="M9 16h4" />
        </>
      );
    default:
      return (
        <>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </>
      );
  }
}

export function AdapterDomainIcon({
  domain,
}: AdapterDomainIconProps): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className="gn-adapters-domain-icon"
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
    >
      <DomainIconPaths domain={domain} />
    </svg>
  );
}
